import { ResponsiveBar } from "@nivo/bar";
import { Box, Flex, Text } from "@chakra-ui/react";
import { getRandomColor } from "../../../Decarbonization/helpers";
import { Card } from "../../../../components/Card";
import * as React from "react";
import CustomAxisTick from "./CustomAxisTick";
import { useTranslation } from "react-i18next";
import { CSRD_QUESTIONNAIRE_TYPES } from "../../../../utils";
import { t } from "i18next";
import { H2 } from "components/text/headings";

const CSRD_PROGRESS_PHASE_COLOURS = Object.freeze({
  [t("csrd.viewProgress.completed")]: "hsla(106,98%,48%,0.49)",
  [t("csrd.viewProgress.inProgress")]: "hsla(50,100%,50%,0.49)",
  [t("csrd.viewProgress.empty")]: "hsla(0,100%,50%,0.62)",
  [t("csrd.viewProgress.notApplicable")]: "hsl(0,0%,76%)",
});

export const ESG_ELEMENTS = Object.freeze({
  [CSRD_QUESTIONNAIRE_TYPES.ENVIRONMENTAL]: {
    symbol: "E",
    text: t(`csrd.questionnaireTypes.${CSRD_QUESTIONNAIRE_TYPES.ENVIRONMENTAL}`),
    color: "#4CAF50",
  },
  [CSRD_QUESTIONNAIRE_TYPES.SOCIAL]: {
    symbol: "S",
    text: t(`csrd.questionnaireTypes.${CSRD_QUESTIONNAIRE_TYPES.SOCIAL}`),
    color: "#1E90FF",
  },
  [CSRD_QUESTIONNAIRE_TYPES.GOVERNANCE]: {
    symbol: "G",
    text: t(`csrd.questionnaireTypes.${CSRD_QUESTIONNAIRE_TYPES.GOVERNANCE}`),
    color: "#34495e",
  },
});

function CSRDProgressChart({ data }) {
  const { t } = useTranslation("translation");

  return data && data.length > 0 ? (
    <Box>
      <Flex gap={6}>
        {Object.keys(CSRD_QUESTIONNAIRE_TYPES).map((type) => {
          return (
            <Flex key={`${type}-legend`} alignItems="center">
              <Text fontWeight="600" padding={1}>
                {ESG_ELEMENTS[type].text}
              </Text>
              <Text
                fontSize="xs"
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="20px"
                height="20px"
                color="white"
                backgroundColor={ESG_ELEMENTS[type].color}
                borderRadius="50%"
                padding={1}
              >
                {ESG_ELEMENTS[type].symbol}
              </Text>
            </Flex>
          );
        })}
      </Flex>
      <Box height={500}>
        <ResponsiveBar
          data={data}
          keys={[
            t("csrd.viewProgress.notApplicable"),
            t("csrd.viewProgress.empty"),
            t("csrd.viewProgress.inProgress"),
            t("csrd.viewProgress.completed"),
          ]}
          indexBy="topic"
          margin={{ top: 20, right: 10, bottom: 90, left: 220 }}
          padding={0.3}
          valueScale={{ type: "linear" }}
          indexScale={{ type: "band", round: true }}
          colors={(item) => CSRD_PROGRESS_PHASE_COLOURS[item.id] ?? getRandomColor()}
          borderColor={{
            from: "color",
            modifiers: [["darker", 1.6]],
          }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 0,
            tickPadding: 10,
            legendPosition: "middle",
            legendOffset: 40,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legendPosition: "middle",
            legendOffset: -50,
            renderTick: (props) => <CustomAxisTick props={props} data={data} />,
          }}
          labelSkipWidth={16}
          labelSkipHeight={16}
          tooltip={(tooltipObj) => {
            const { value, indexValue } = tooltipObj;

            return (
              <Box position="relative">
                <Card position="absolute" minWidth={"200px"} padding={3} right={"100%"} bottom={"100%"}>
                  <Text fontSize="sm">{indexValue}</Text>
                  <Text fontSize="sm">
                    <b>
                      {tooltipObj.id}: {value}
                    </b>
                  </Text>
                </Card>
              </Box>
            );
          }}
          legends={[
            {
              dataFrom: "keys",
              anchor: "bottom",
              direction: "row",
              justify: false,
              translateX: 27,
              translateY: 70,
              itemsSpacing: 31,
              itemWidth: 76,
              itemHeight: 19,
              itemDirection: "left-to-right",
              itemOpacity: 0.85,
              symbolSize: 20,
              effects: [
                {
                  on: "hover",
                  style: {
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ]}
          role="application"
          ariaLabel="CSRD Progress chart"
          layout="horizontal"
          enableGridY={false}
          enableGridX={true}
        />
      </Box>
    </Box>
  ) : (
    <H2>{t("noDataAvailable")}</H2>
  );
}

export default CSRDProgressChart;
